import { useCallback, useEffect, useState } from 'react';

export type ThemeChoice = 'light' | 'dark';

export const THEME_CHOICES: readonly ThemeChoice[] = ['light', 'dark'];

const STORAGE_KEY = 'minesweeper.theme.v1';

function systemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
}

/**
 * Reads the stored choice, folding away the themes that no longer exist.
 *
 * There were briefly four: light, a cool blue-grey dark, a pure black/white
 * dark, and a red one. Two are gone, and the surviving dark theme *is* the
 * blue-grey one — so anyone who had picked "blue" is already exactly where
 * they should be, and the two removed choices map onto it rather than
 * dropping those visitors back to light.
 */
function readStored(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light') return 'light';
    if (stored === 'dark' || stored === 'blue' || stored === 'red') return 'dark';
  } catch {
    // Private browsing; fall through to the system's own preference.
  }
  return systemPrefersDark() ? 'dark' : 'light';
}

/**
 * Theme preference: light or dark, written to `data-theme` on the root
 * element. There is no "match the system" option — a page that silently
 * repaints itself when the OS theme changes was one of the things being
 * flagged as buggy, and a plain, explicit choice removes the ambiguity
 * outright. The system's light/dark preference still decides the default on
 * a first visit, before anything has been chosen (see `readStored`).
 */
export function useTheme(): { theme: ThemeChoice; setTheme: (theme: ThemeChoice) => void } {
  const [theme, setThemeState] = useState<ThemeChoice>(readStored);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Rewrite a legacy 'blue'/'red' value so the stored string stays in the
    // vocabulary the rest of the app knows about.
    try {
      if (window.localStorage.getItem(STORAGE_KEY) !== theme) {
        window.localStorage.setItem(STORAGE_KEY, theme);
      }
    } catch {
      // Private browsing; the in-memory choice is still correct.
    }
  }, [theme]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, []);

  return { theme, setTheme };
}

export type PerfMode = 'auto' | 'low';

export const PERF_MODES: readonly PerfMode[] = ['auto', 'low'];

const PERF_STORAGE_KEY = 'minesweeper.perf.v1';

function readStoredPerf(): PerfMode {
  try {
    const stored = window.localStorage.getItem(PERF_STORAGE_KEY);
    if (stored === 'auto' || stored === 'low') return stored;
  } catch {
    // Private browsing; 'auto' is a fine default either way.
  }
  return 'auto';
}

/**
 * Strips blur and animation for low-spec machines — see the `[data-perf]`
 * rules in styles.css for what it actually turns off. Off by default: the
 * glass look is the point on hardware that can afford it, so this is
 * something a player opts into, not something guessed from the device.
 */
export function usePerfMode(): { perf: PerfMode; setPerf: (perf: PerfMode) => void } {
  const [perf, setPerfState] = useState<PerfMode>(readStoredPerf);

  useEffect(() => {
    if (perf === 'low') document.documentElement.setAttribute('data-perf', 'low');
    else document.documentElement.removeAttribute('data-perf');
  }, [perf]);

  const setPerf = useCallback((next: PerfMode) => {
    setPerfState(next);
    try {
      window.localStorage.setItem(PERF_STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, []);

  return { perf, setPerf };
}

const FLAG_MODE_STORAGE_KEY = 'minesweeper.flagmode.v1';

function readStoredFlagMode(): boolean {
  try {
    const stored = window.localStorage.getItem(FLAG_MODE_STORAGE_KEY);
    // No stored choice yet — including private browsing, where getItem can
    // also just throw below — defaults on, not off.
    if (stored === null) return true;
    return stored === '1';
  } catch {
    return true;
  }
}

/**
 * Swaps what a plain tap/click does on an unrevealed cell: flag instead of
 * reveal, with the long-press/right-click gesture becoming the reveal
 * instead. On by default: a stray tap only plants a flag rather than
 * risking a reveal — cheap to undo — which matters most on exactly the
 * touch/trackpad input this mode was built for. Anyone who prefers the
 * classic tap-to-reveal scheme can switch it off, and that choice persists.
 */
export function useFlagMode(): { flagMode: boolean; setFlagMode: (flagMode: boolean) => void } {
  const [flagMode, setFlagModeState] = useState<boolean>(readStoredFlagMode);

  const setFlagMode = useCallback((next: boolean) => {
    setFlagModeState(next);
    try {
      window.localStorage.setItem(FLAG_MODE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, []);

  return { flagMode, setFlagMode };
}

/** Short, distinct patterns so a win and a loss feel different in your hand. */
export const HAPTICS = {
  flag: [18],
  reveal: [8],
  win: [24, 40, 24, 40, 60],
  lose: [90, 50, 160],
} as const;

export function vibrate(pattern: readonly number[]): void {
  navigator.vibrate?.([...pattern]);
}
